/**
 * A formatação de uma petição do advogado (Word ou PDF) → o que o editor forense
 * aplica (`docs/PLANO_FORMATACAO_DO_ARQUIVO.md`). Quem mede é o servidor
 * (`POST /editor/formatacao/extrair`), sem guardar o arquivo; aqui o que veio
 * medido vira formato de parágrafo e página — e só o que veio medido.
 */
import { extrairFormatacao, type FormatacaoMedida } from '@/api/editor';
import { FONTES, formatoSeguro, type ExtracaoDeArquivo, type FormatoParagrafo, type Margens } from '@/components/editor-forense';
import { getUserVisibleErrorMessage } from '@/lib/errors';

const so = (nome: string) => nome.toLowerCase().replace(/[^a-z]/g, '');

// Como o arquivo chama a fonte → a do editor. O PDF traz o nome PostScript
// ("TimesNewRomanPSMT", "Times" das base14) ou a equivalente métrica que a
// máquina de quem gerou tinha ("LiberationSerif", "Arimo", "Carlito").
const APELIDOS: [string, string][] = [
  // Outra fonte com o começo de uma do editor: sem par.
  ['arialnarrow', ''], ['arialblack', ''],
  ['arial', 'Arial'], ['helvetica', 'Arial'], ['arimo', 'Arial'], ['liberationsans', 'Arial'],
  ['times', 'Times New Roman'], ['tinos', 'Times New Roman'], ['liberationserif', 'Times New Roman'],
  ['calibri', 'Calibri'], ['carlito', 'Calibri'],
  ['garamond', 'Garamond'], ['ebgaramond', 'Garamond'], ['adobegaramond', 'Garamond'],
  ['verdana', 'Verdana'],
];

/** A fonte do editor que corresponde à do arquivo; `undefined` se o editor não a tem. */
export function fonteDoEditor(familia: string): string | undefined {
  const chave = so(familia);
  const apelido = APELIDOS.find(([inicio]) => chave.startsWith(inicio))?.[1];
  return apelido && FONTES.some((f) => f.nome === apelido) ? apelido : undefined;
}

export function paraExtracao(m: FormatacaoMedida): ExtracaoDeArquivo {
  const formato: Partial<FormatoParagrafo> = {};
  const avisos: string[] = [];
  const { familia, tamanho } = m.fonte;
  if (familia) {
    const nome = fonteDoEditor(familia);
    if (nome) formato.fonte = nome;
    else avisos.push(`A fonte “${familia}” não está no editor: o resto é aplicado, a fonte da peça fica como está.`);
  }
  // Meio ponto: é o passo do Word e do seletor de tamanho.
  if (tamanho) formato.tamanho = Math.round(tamanho * 2) / 2;
  const p = m.paragrafo;
  if (p.alinhamento) formato.alinhamento = p.alinhamento;
  if (p.entrelinha) formato.entrelinha = p.entrelinha;
  if (p.especial) {
    formato.especial = p.especial;
    formato.medida = p.especial === 'nenhum' ? 0 : (p.medida ?? 0);
  }
  if (p.recuoEsquerdo !== null) formato.recuoEsquerdo = p.recuoEsquerdo;
  if (p.recuoDireito !== null) formato.recuoDireito = p.recuoDireito;
  if (p.espacoAntes !== null) formato.espacoAntes = p.espacoAntes;
  if (p.espacoDepois !== null) formato.espacoDepois = p.espacoDepois;

  const documento: ExtracaoDeArquivo['documento'] = {};
  if (m.pagina) {
    const margens: Partial<Margens> = {};
    (Object.keys(m.pagina.margens) as (keyof Margens)[]).forEach((k) => {
      const v = m.pagina!.margens[k];
      if (v !== null) margens[k] = v;
    });
    if (Object.keys(margens).length) documento.margens = margens;
    if (m.pagina.papel) documento.papel = m.pagina.papel;
    documento.orientacao = m.pagina.orientacao;
  }
  const t = m.timbrado;
  const timbrado = t && (t.cabecalho || t.rodape || t.numeroDePagina || t.imagemCabecalho || t.imagemRodape || t.logotipo)
    ? {
        cabecalho: t.cabecalho ?? '',
        rodape: t.rodape ?? '',
        numeroDePagina: t.numeroDePagina,
        ...(t.imagemCabecalho ? { imagemCabecalho: t.imagemCabecalho } : {}),
        ...(t.imagemRodape ? { imagemRodape: t.imagemRodape } : {}),
        ...(t.logotipo ? { logotipo: t.logotipo } : {}),
      }
    : undefined;
  // Nas faixas do editor, como todo formato que entra na peça.
  return { formato: formatoSeguro(formato) ?? {}, documento, avisos, ...(timbrado ? { timbrado } : {}) };
}

/** O `extrairFormatacao` do editor: rejeita com a mensagem que o painel mostra. */
export async function extrairDeArquivo(arquivo: File): Promise<ExtracaoDeArquivo> {
  try {
    return paraExtracao(await extrairFormatacao(arquivo));
  } catch (err) {
    throw new Error(getUserVisibleErrorMessage(err) ?? `Não consegui ler “${arquivo.name}”.`);
  }
}
